import { useAuthQuery } from '@/api/index.ts';
import { studyQueryKeys } from '@/config/queryKeys';
import { canUserEditStudy } from '@/config/studies';
import { StudyInfoSection } from '@/pages/Study/components/StudyInfoSection';
import { StudyTitleBar } from '@/pages/Study/components/StudyTitleBar';
import { TauSamplesDetailTable } from '@/pages/Study/components/TauSamplesDetailTable';
import { fetchStudy } from '@/services/studies';
import { useAppStore } from '@/stores/app';
import { Study } from '@/types';
import { FullLoading } from '@components/index';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { useSuspenseQuery } from '@tanstack/react-query';
import { ErrorComponent, createLazyFileRoute } from '@tanstack/react-router';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import studyStyles from '../../pages/Studies/styles.module.css';
import '../../styles/components/ag-grid-overrides.css';
import detailsPageStyles from '../../styles/components/details.page.module.css';
import pageStyles from '../../styles/pages/index.module.css';

export const Route = createLazyFileRoute('/studies/$studyId_/tau-samples')({
  component: RouteComponent,
  errorComponent: e => <ErrorComponent {...e} />,
  pendingComponent: () => {
    const { t } = useTranslation('studies');
    return <FullLoading label={`${t('fetching')}...`} />;
  },
});

function RouteComponent() {
  const { t } = useTranslation('studies');
  const selectedOrg = useAppStore(useShallow(state => state.selectedOrganization));
  const { studyId } = Route.useParams();

  if (!selectedOrg?.id) {
    return <ErrorComponent error={new Error(t('study.no_org_selected'))} />;
  }

  const orgId = selectedOrg.id;

  const { data, isLoading } = useSuspenseQuery<Study>({
    queryKey: studyQueryKeys.detail(orgId, studyId),
    queryFn: () => fetchStudy(orgId, studyId),
  });

  const { data: currentUser } = useAuthQuery();
  const canEdit = canUserEditStudy(data, currentUser ?? null);
  const emptyTxt = t('empty', { ns: 'general' });

  const tauAssay = data?.assays?.find(a => a.assay_version === 'Tau');

  return (
    <Stack as="section" className={studyStyles.container} fullWidth orientation="vertical">
      <StudyTitleBar
        canEdit={canEdit}
        data={data}
        detailTitle={t('study.tauDetailTitle')}
        orgId={orgId}
        pageStyles={pageStyles}
        studyStyles={studyStyles}
        t={t}
      />
      <Stack as="header" className={detailsPageStyles.header}>
        <StudyInfoSection data={data} emptyTxt={emptyTxt} isLoading={isLoading} t={t} />
      </Stack>

      <Stack fullWidth gap={4} orientation="vertical">
        <Stack alignItems="center" fullWidth justifyContent="space-between" orientation="horizontal">
          <Text as="h3">{t('study.tauDetailTitle')}</Text>
          <Stack gap={4} orientation="horizontal">
            <Text weight="bold">Total Samples:</Text>
            <Text>{tauAssay?.num_samples ?? 0}</Text>
            <Text weight="bold">Proteoforms:</Text>
            <Text>{data?.num_proteoform_groups ?? 0}</Text>
            {/* TODO: backend to expose ptm/isoform/threshold counts on StudyPublicGetOne */}
          </Stack>
        </Stack>
        <TauSamplesDetailTable orgId={orgId} studyId={studyId} />
      </Stack>
    </Stack>
  );
}
